/** Swipe-up to open / drag-down to close for mobile bottom sheets. */

const LOCK_PX = 6;
const OPEN_PX = 28;
const CLOSE_PX = 80;
const VELOCITY = 0.42;
const SUPPRESS_CLICK_AFTER_DRAG_PX = 16;

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const closestMatch = (node, selector) => {
  if (!selector || !(node instanceof Element)) return null;
  return node.closest(selector);
};

const suppressNextClick = (el) => {
  if (!el) return;
  const stop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    el.removeEventListener('click', stop, true);
  };
  el.addEventListener('click', stop, true);
  window.setTimeout(() => el.removeEventListener('click', stop, true), 450);
};

/**
 * @param {object} options
 * @param {HTMLElement} [options.peek] Collapsed bar — swipe up opens the sheet
 * @param {HTMLElement} options.drawer Overlay root
 * @param {HTMLElement} options.panel Sliding panel
 * @param {HTMLElement} [options.backdrop]
 * @param {HTMLElement} [options.scrollEl] Inner scroller (drag-down only at top)
 * @param {() => void} options.open
 * @param {() => void} options.close
 * @param {() => boolean} options.isOpen
 * @param {string} [options.ignorePeekClosest]
 * @param {string} [options.handleClosest]
 */
export const attachBottomSheetSwipe = ({
  peek,
  drawer,
  panel,
  backdrop,
  scrollEl,
  open,
  close,
  isOpen,
  ignorePeekClosest = 'a, button, input, select, textarea',
  handleClosest = '.js-bottom-sheet-handle, [data-bottom-sheet-handle]',
}) => {
  if (!drawer || !panel || typeof open !== 'function' || typeof close !== 'function') {
    return () => {};
  }

  let drag = null;

  const panelHeight = () => {
    const height = panel.getBoundingClientRect().height;
    return height > 0 ? height : Math.min(window.innerHeight * 0.88, 720);
  };

  const setOffset = (px, height) => {
    const offset = clamp(px, 0, height);
    panel.style.transform = `translate3d(0, ${offset}px, 0)`;
    if (backdrop) {
      backdrop.style.opacity = String(clamp(1 - offset / height, 0, 1));
    }
  };

  const clearInline = () => {
    panel.classList.remove('is-dragging');
    panel.style.transform = '';
    panel.style.transition = '';
    if (backdrop) backdrop.style.opacity = '';
  };

  const preparePeekOpen = () => {
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-dragging');
    const height = panelHeight();
    setOffset(height, height);
    return height;
  };

  const finishOpen = () => {
    const offset = panel.style.transform;
    const dim = backdrop?.style.opacity;
    panel.classList.remove('is-dragging');
    open();
    panel.style.transform = offset;
    if (backdrop && dim) backdrop.style.opacity = dim;
    window.requestAnimationFrame(() => {
      panel.style.transform = '';
      if (backdrop) backdrop.style.opacity = '';
    });
  };

  const snapBackOpen = () => {
    panel.classList.remove('is-dragging');
    window.requestAnimationFrame(() => {
      panel.style.transform = '';
      if (backdrop) backdrop.style.opacity = '';
    });
  };

  const abortPeekPreview = () => {
    const height = panelHeight();
    panel.classList.remove('is-dragging');
    panel.style.transform = `translate3d(0, ${height}px, 0)`;
    if (backdrop) backdrop.style.opacity = '0';
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      clearInline();
      if (!isOpen()) {
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
      }
    };
    panel.addEventListener('transitionend', (event) => {
      if (event.target === panel && event.propertyName === 'transform') settle();
    }, { once: true });
    window.setTimeout(settle, 450);
  };

  const onPointerMove = (event) => {
    if (!drag || event.pointerId !== drag.pointerId || drag.cancelled) return;
    const y = event.clientY;
    const x = event.clientX;
    const dy = y - drag.startY;
    const dx = x - drag.startX;
    const now = event.timeStamp;

    if (!drag.active) {
      if (Math.abs(dy) < LOCK_PX && Math.abs(dx) < LOCK_PX) return;
      if (
        Math.abs(dx) > Math.abs(dy)
        || (drag.mode === 'open' && dy > 0)
        || (drag.mode === 'close' && dy < 0)
        || (drag.mode === 'close' && !drag.fromHandle && drag.scrollTop > 0 && dy > 0)
      ) {
        drag.cancelled = true;
        return;
      }

      drag.active = true;
      drag.originY = y;
      drag.originT = now;
      try {
        drag.source.setPointerCapture?.(event.pointerId);
      } catch {
        /* Safari: capture can fail if the node is not a pointer target. */
      }
      if (drag.mode === 'open') {
        drag.height = preparePeekOpen();
      } else {
        panel.classList.add('is-dragging');
        drag.height = panelHeight();
      }
    }

    event.preventDefault();
    drag.lastY = y;
    drag.lastT = now;

    if (drag.mode === 'open') {
      const pulled = clamp(drag.startY - y, 0, drag.height);
      setOffset(drag.height - pulled, drag.height);
      return;
    }

    setOffset(clamp(y - drag.startY, 0, drag.height), drag.height);
  };

  const onPointerUp = (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);

    const current = drag;
    drag = null;

    if (!current.active || current.cancelled) return;

    const y = event.clientY;
    const dt = Math.max(1, (event.timeStamp || current.lastT) - current.originT);
    const velocity = (y - current.originY) / dt;
    const height = current.height || panelHeight();

    if (current.mode === 'open') {
      const pulled = clamp(current.startY - y, 0, height);
      const shouldOpen = pulled > OPEN_PX || velocity < -VELOCITY;
      if (pulled >= SUPPRESS_CLICK_AFTER_DRAG_PX) {
        suppressNextClick(current.source);
      }
      if (shouldOpen) finishOpen();
      else abortPeekPreview();
      return;
    }

    const down = clamp(y - current.startY, 0, height);
    const shouldClose = down > CLOSE_PX || velocity > VELOCITY;
    if (down >= SUPPRESS_CLICK_AFTER_DRAG_PX) {
      suppressNextClick(current.source);
    }
    if (shouldClose) {
      panel.classList.remove('is-dragging');
      panel.style.transform = `translate3d(0, ${height}px, 0)`;
      if (backdrop) backdrop.style.opacity = '0';
      close();
    } else {
      snapBackOpen();
    }
  };

  const startDrag = (event, mode, source, { fromHandle = false } = {}) => {
    if (prefersReducedMotion()) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (drag) return;

    drag = {
      pointerId: event.pointerId,
      mode,
      source,
      fromHandle,
      startX: event.clientX,
      startY: event.clientY,
      lastY: event.clientY,
      lastT: event.timeStamp,
      originY: event.clientY,
      originT: event.timeStamp,
      scrollTop: fromHandle ? 0 : (scrollEl?.scrollTop || 0),
      cancelled: false,
      active: false,
      height: 0,
    };

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  const onPeekPointerDown = (event) => {
    if (closestMatch(event.target, ignorePeekClosest)) return;
    if (isOpen()) {
      startDrag(event, 'close', peek, { fromHandle: true });
      return;
    }
    startDrag(event, 'open', peek);
  };

  const onPanelPointerDown = (event) => {
    if (!isOpen()) return;
    if (closestMatch(event.target, '.js-sticky-summary-close, .js-booking-details-close')) return;

    const onHandle = Boolean(closestMatch(event.target, handleClosest));
    const onInteractive = Boolean(
      closestMatch(event.target, 'a, button, input, select, textarea, label, [role="button"]')
    );
    if (!onHandle && onInteractive) return;

    startDrag(event, 'close', panel, { fromHandle: onHandle });
  };

  const onScrollPointerDown = (event) => {
    if (!isOpen() || !scrollEl || !scrollEl.contains(event.target)) return;
    if (closestMatch(event.target, handleClosest)) return;
    if (closestMatch(event.target, '.js-sticky-summary-close, .js-booking-details-close')) return;
    if (closestMatch(event.target, 'a, button, input, select, textarea, label, [role="button"]')) return;
    if ((scrollEl.scrollTop || 0) > 0) return;

    startDrag(event, 'close', scrollEl, { fromHandle: false });
  };

  peek?.addEventListener('pointerdown', onPeekPointerDown);
  panel.addEventListener('pointerdown', onPanelPointerDown);
  scrollEl?.addEventListener('pointerdown', onScrollPointerDown);

  return () => {
    drag = null;
    peek?.removeEventListener('pointerdown', onPeekPointerDown);
    panel.removeEventListener('pointerdown', onPanelPointerDown);
    scrollEl?.removeEventListener('pointerdown', onScrollPointerDown);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    clearInline();
  };
};
